import { useEffect, useMemo, useState } from 'react';
import { subscribeToLiveClients } from '../services/liveClientsStore';
import { subscribeToMyAssignedWorkflowTasks } from '../services/workflowFirestoreService';
import { buildExcludedTaskClientIdSet, isTaskOverdue } from '../utils/taskUtils';

/**
 * Count of the current user's open assigned workflow tasks that are overdue
 * (excludes DNQ and archived clients).
 */
export function useMyAssignedOverdueCount(user, { enabled = true } = {}) {
  const [workflowTasks, setWorkflowTasks] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    if (!enabled || !user?.email) {
      setWorkflowTasks([]);
      return () => {};
    }
    return subscribeToMyAssignedWorkflowTasks(user.email, setWorkflowTasks);
  }, [enabled, user?.email]);

  useEffect(() => {
    if (!enabled || !user?.uid) {
      setClients([]);
      return () => {};
    }
    return subscribeToLiveClients((result) => {
      setClients(result.success ? (result.clients || []) : []);
    });
  }, [enabled, user?.uid]);

  return useMemo(() => {
    if (!user?.email) return 0;
    const excludedIds = buildExcludedTaskClientIdSet(clients);
    return workflowTasks.filter((task) => {
      if (!task || task.status !== 'open') return false;
      if (task.clientId && excludedIds.has(task.clientId)) return false;
      return isTaskOverdue(task);
    }).length;
  }, [workflowTasks, clients, user?.email]);
}
